import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog"
import { Button } from "./ui/button"
import { Alert, AlertDescription } from "./ui/alert"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip"
import {
  X,
  Download,
  Printer,
  ZoomIn,
  ZoomOut,
  ChevronLeft,
  ChevronRight,
  Maximize,
  Minimize,
  Loader2,
  FileText
} from 'lucide-react';
import { Document, Page, pdfjs } from 'react-pdf';
import axios from 'axios';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

const PdfViewer = ({ open, onClose, file }) => {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfBlob, setPdfBlob] = useState(null);

  // Fetch PDF with authentication
  useEffect(() => {
    if (open && file?.downloadUrl) {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');

      axios.get(file.downloadUrl, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        responseType: 'blob'
      })
        .then(response => {
          const blob = new Blob([response.data], { type: 'application/pdf' });
          const url = URL.createObjectURL(blob);
          setPdfBlob(url);
        })
        .catch(err => {
          console.error('Error fetching PDF:', err);
          setError('Failed to load PDF file. Please check your connection and try again.');
          setLoading(false);
        });
    }

    return () => {
      if (pdfBlob) {
        URL.revokeObjectURL(pdfBlob);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, file?.downloadUrl]);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setLoading(false);
    setError(null);
  };

  const onDocumentLoadError = (error) => {
    console.error('Error loading PDF:', error);
    setError('Failed to load PDF file. The file may be corrupted or invalid.');
    setLoading(false);
  };

  const goToPreviousPage = () => setPageNumber((prev) => Math.max(prev - 1, 1));
  const goToNextPage = () => setPageNumber((prev) => Math.min(prev + 1, numPages));

  const handleZoomIn = () => setScale((prev) => Math.min(prev + 0.2, 3.0));
  const handleZoomOut = () => setScale((prev) => Math.max(prev - 0.2, 0.5));
  const handleResetZoom = () => setScale(1.0);
  const toggleFullscreen = () => setIsFullscreen(!isFullscreen);

  const handleDownload = () => {
    if (pdfBlob && file) {
      const link = document.createElement('a');
      link.href = pdfBlob;
      link.download = file.name || 'document.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handlePrint = () => {
    if (pdfBlob) {
      const printWindow = window.open(pdfBlob, '_blank');
      if (printWindow) {
        printWindow.addEventListener('load', () => {
          printWindow.print();
        });
      }
    }
  };

  const handleClose = () => {
    setPageNumber(1);
    setScale(1.0);
    setIsFullscreen(false);
    setLoading(true);
    setError(null);
    setNumPages(null);
    if (pdfBlob) {
      URL.revokeObjectURL(pdfBlob);
      setPdfBlob(null);
    }
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(val) => !val && handleClose()}>
      <DialogContent className={`flex flex-col p-0 gap-0 border-none ${isFullscreen ? 'w-screen h-screen max-w-none rounded-none' : 'w-[90vw] h-[90vh] max-w-6xl'}`}>
        {/* Header */}
        <div className="flex items-center justify-between border-b px-4 py-3 bg-gray-50">
          <div className="flex items-center gap-2 overflow-hidden">
            <FileText className="h-5 w-5 text-red-500 shrink-0" />
            <h3 className="font-semibold text-gray-900 truncate max-w-md">
              {file?.name || 'PDF Document'}
            </h3>
          </div>
          <Button variant="ghost" size="icon" onClick={handleClose} className="h-8 w-8 hover:bg-gray-200 rounded-full">
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Toolbar */}
        <div className="flex items-center justify-between border-b px-4 py-2 bg-white flex-wrap gap-2">
          <div className="flex items-center gap-1 bg-gray-100 rounded-md p-0.5">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" onClick={goToPreviousPage} disabled={pageNumber <= 1 || loading} className="h-8 w-8">
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Previous Page</TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <span className="text-sm font-medium px-2 min-w-[80px] text-center">
              {loading ? '...' : `${pageNumber} / ${numPages || '-'}`}
            </span>

            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" onClick={goToNextPage} disabled={pageNumber >= numPages || loading} className="h-8 w-8">
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Next Page</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          <div className="flex items-center gap-1 bg-gray-100 rounded-md p-0.5">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" onClick={handleZoomOut} disabled={loading} className="h-8 w-8">
                    <ZoomOut className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Zoom Out</TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <button
              onClick={handleResetZoom}
              className="text-sm font-medium px-2 min-w-[60px] text-center hover:bg-gray-200 rounded h-8 transition-colors"
            >
              {Math.round(scale * 100)}%
            </button>

            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" onClick={handleZoomIn} disabled={loading} className="h-8 w-8">
                    <ZoomIn className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Zoom In</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          <div className="flex items-center gap-2 ml-auto">
            <Button variant="outline" size="sm" onClick={handleDownload} className="gap-2 hidden sm:flex">
              <Download className="h-4 w-4" />
              <span className="hidden sm:inline">Download</span>
            </Button>

            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" onClick={handlePrint} className="h-9 w-9">
                    <Printer className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Print</TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" onClick={toggleFullscreen} className="h-9 w-9">
                    {isFullscreen ? <Minimize className="h-4 w-4" /> : <Maximize className="h-4 w-4" />}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 bg-slate-700 overflow-auto flex flex-col items-center justify-start p-4 relative min-h-0">
          {loading && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-white gap-3 z-10 bg-slate-700/80">
              <Loader2 className="h-10 w-10 animate-spin" />
              <p>Loading PDF...</p>
            </div>
          )}

          {error && (
            <div className="w-full max-w-lg mt-10">
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
              <Button onClick={handleDownload} className="w-full mt-4" variant="secondary">
                <Download className="h-4 w-4 mr-2" /> Download PDF Instead
              </Button>
            </div>
          )}

          {pdfBlob && (
            <Document
              file={pdfBlob}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={onDocumentLoadError}
              loading={<Loader2 className="h-8 w-8 animate-spin text-white" />}
            >
              <Page
                pageNumber={pageNumber}
                scale={scale}
                renderTextLayer={false}
                renderAnnotationLayer={false}
                className="shadow-2xl mb-4"
                height={isFullscreen ? window.innerHeight - 150 : 600}
              />
            </Document>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PdfViewer;